import type { MaintenanceEvent } from '~/api/types'
import { Markdown } from '~/components/ui/markdown'
import { Typewriter } from '~/components/chat/typewriter'
import { Message } from './message'
import { ResultCard } from './result-card'
import type { ResultItem } from './types'
import { toServiceTone } from './utils'

/** 완료된 변경 영향 분석의 사용자 입력, 요약, 결과 카드를 표시한다. */
export function ActiveAnalysisMessages({
    activeEvent,
    resultItems,
    checklistCount,
    onSelectImpact,
    onCopyRecheckItems,
    onOpenReport,
}: {
    activeEvent: MaintenanceEvent
    resultItems: ResultItem[]
    checklistCount: number
    onSelectImpact: (itemId: string) => void
    onCopyRecheckItems: () => void
    onOpenReport: (opts?: { print?: boolean }) => void
}) {
    // 대화 메시지는 created_at(도착순) 기준으로 안정 정렬한다.
    // created_at 이 같거나 비면 원래 배열 인덱스로 tie-break 해 순서가 섞이지 않게 한다.
    const conversation = (activeEvent.impact.conversation ?? [])
        .map((turn, index) => ({ turn, index }))
        .sort((a, b) => {
            const ta = a.turn.created_at ?? ''
            const tb = b.turn.created_at ?? ''
            if (ta && tb && ta !== tb) return ta < tb ? -1 : 1
            return a.index - b.index
        })
        .map(({ turn }) => turn)

    // 가장 마지막 봇 메시지 1건만 타이핑 효과를 준다(과거 메시지 재타이핑 방지).
    // 후속 대화 봇 턴이 있으면 그 마지막 턴이, 없으면 선두 guide가 최신 봇 메시지다.
    const lastBotTurnIndex = conversation.reduce(
        (last, turn, index) => (turn.role !== 'user' ? index : last),
        -1
    )

    return (
        <>
            <Message from='user'>
                <p className='whitespace-pre-wrap'>
                    {activeEvent.changeDescription}
                </p>
            </Message>
            <Message from='bot'>
                <div className='grid gap-2'>
                    <p>
                        변경 내용을 기준으로 체크리스트와 매뉴얼 근거를
                        대조했습니다.
                    </p>
                    {activeEvent.impact.guide ? (
                        <div className='text-foreground font-medium'>
                            {lastBotTurnIndex === -1 ? (
                                <Typewriter
                                    text={toServiceTone(
                                        activeEvent.impact.guide
                                    )}
                                />
                            ) : (
                                <Markdown>
                                    {toServiceTone(activeEvent.impact.guide)}
                                </Markdown>
                            )}
                        </div>
                    ) : null}
                </div>
            </Message>
            {conversation.map((turn, index) => (
                <Message
                    key={`${index}-${turn.created_at ?? ''}-${turn.role}`}
                    from={turn.role === 'user' ? 'user' : 'bot'}>
                    {turn.role === 'user' ? (
                        <p className='whitespace-pre-wrap'>{turn.content}</p>
                    ) : index === lastBotTurnIndex ? (
                        <Typewriter text={turn.content} />
                    ) : (
                        <Markdown>{turn.content}</Markdown>
                    )}
                </Message>
            ))}
            <Message from='bot' wide>
                <p>
                    아래로 분석 결과를 정리했어요. 항목별로 현재와 변경 후,
                    매뉴얼 조항, 권고 조치를 함께 확인하세요.
                </p>
                <ResultCard
                    event={activeEvent}
                    items={resultItems}
                    checklistCount={checklistCount}
                    onSelect={onSelectImpact}
                    onCopy={onCopyRecheckItems}
                    onOpenReport={onOpenReport}
                />
            </Message>
        </>
    )
}
